/**
 * Per-thread history floors, client side (GT-01).
 *
 * A member added to an existing group thread must not read the transcript from
 * before they joined. The enforcement lives in firestore.rules, which compares
 * each message's `orderTs` against the caller's floor in the server-owned
 * `historyFloorByUid` map. This module is the client half of that contract.
 *
 * WHY A PUBLISHED REGISTRY. The overlay read happens deep in the repository
 * layer, which has neither the accountId nor the membership in scope — the same
 * constraint that made `setGroupThreadReadAccount` a published module-level
 * value. Floors arrive with the membership fetch (`threadMembershipRead`, the
 * ONLY way a browser can learn its floor: rules deny client reads of
 * `prest0n_thread_participants` outright) and are published here for the
 * repository to consult.
 *
 * WHY THE CLIENT MUST FILTER AT ALL, given rules already enforce it: a Firestore
 * `list` fails WHOLESALE if any single candidate document is denied. An
 * unfiltered overlay query on a thread with pre-join messages is therefore a
 * hard permission-denied for the whole transcript, not a truncation — the late
 * joiner would see nothing instead of seeing their own messages. The
 * `where('orderTs','>=',floor)` filter keeps the candidate set inside what the
 * rules allow, so the query succeeds and simply returns less.
 *
 * FAIL CLOSED. An unknown thread returns MAX_SAFE_INTEGER, so a query built
 * before floors are published returns nothing rather than everything. 0 (full
 * history) is only ever used when the server actually said 0.
 */

/** Hide everything. Mirrors the server's `HISTORY_FLOOR_FAIL_CLOSED`. */
export const HISTORY_FLOOR_FAIL_CLOSED = Number.MAX_SAFE_INTEGER;

let floorsByThreadId = new Map<string, number>();

/**
 * Version counter, bumped on every publish/clear whose content CHANGED
 * (2026-08-26 real-time work). The overlay subscription bakes the floor into
 * its Firestore query at attach time; before this counter existed there was
 * no signal that a floor had resolved, so a listener attached during the
 * fail-closed window (floor = MAX_SAFE_INTEGER, matching nothing) stayed
 * attached to an empty query forever. Consumers put the version in their
 * effect deps and re-attach when it moves.
 */
let floorsVersion = 0;

export function getGroupThreadHistoryFloorsVersion(): number {
  return floorsVersion;
}

const versionListeners = new Set<(version: number) => void>();

/** Notify on version movement. Returns the unsubscribe. */
export function subscribeGroupThreadHistoryFloors(listener: (version: number) => void): () => void {
  versionListeners.add(listener);
  return () => versionListeners.delete(listener);
}

function floorsEqual(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) return false;
  let equal = true;
  a.forEach((value, key) => {
    if (b.get(key) !== value) equal = false;
  });
  return equal;
}

function bumpVersion(): void {
  floorsVersion += 1;
  versionListeners.forEach((listener) => {
    try {
      listener(floorsVersion);
    } catch { /* one bad listener must not mute the rest */ }
  });
}

/** Accept only the shape the rules will accept: a non-negative integer. */
function sanitizeFloor(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) return HISTORY_FLOOR_FAIL_CLOSED;
  return raw;
}

/**
 * Publish the caller's floors, replacing the previous set wholesale.
 *
 * Replacement (not merge) is deliberate: on sign-out or account switch the
 * previous user's floors must not survive to widen the next user's reads. The
 * membership fetch always returns the caller's complete thread set, so a
 * replace is also always complete.
 */
export function setGroupThreadHistoryFloors(
  rows: ReadonlyArray<{ threadId?: unknown; historyFloor?: unknown }> | null | undefined,
): void {
  const next = new Map<string, number>();
  for (const row of rows || []) {
    const threadId = String(row?.threadId ?? '').trim();
    if (!threadId) continue;
    next.set(threadId, sanitizeFloor(row?.historyFloor));
  }
  const changed = !floorsEqual(floorsByThreadId, next);
  floorsByThreadId = next;
  // Bump only on real change: every focus-driven membership refresh republishes
  // the same floors, and a version that moved on each would re-attach the
  // overlay listener for nothing.
  if (changed) bumpVersion();
}

/** Drop every published floor (sign-out). Subsequent reads fail closed. */
export function clearGroupThreadHistoryFloors(): void {
  const hadFloors = floorsByThreadId.size > 0;
  floorsByThreadId = new Map<string, number>();
  if (hadFloors) bumpVersion();
}

/**
 * The caller's floor for `threadId`, or the fail-closed sentinel when no floor
 * has been published for it.
 */
export function getGroupThreadHistoryFloor(threadId: string): number {
  const id = (threadId || '').trim();
  if (!id) return HISTORY_FLOOR_FAIL_CLOSED;
  const floor = floorsByThreadId.get(id);
  return floor === undefined ? HISTORY_FLOOR_FAIL_CLOSED : floor;
}
